import { MANUFACTURER_IDS } from "./manufacturers.js";
import { TIER_STAT_MULT, type LocalizedText, type Manufacturer, type Tier } from "../schema/common.js";

/**
 * Special bonuses on equipment (D77). Substats make a module stronger; a special bonus makes it *behave*
 * differently in a fight — the limit bar fills faster, a wounded enemy is finished off, a hit heals the
 * frame that landed it. Every workshop can roll every bonus, but each one has its speciality, so a
 * Kurogane blade is the piece a player hunts when they want limit breaks.
 */
export const AFFIX_KEYS = [
  "limit_charge",
  "limit_power",
  "opening_burst",
  "last_stand",
  "executioner",
  "lifesteal",
  "thorns",
  "shieldbreaker",
] as const;
export type AffixKey = (typeof AFFIX_KEYS)[number];

export interface AffixDef {
  key: AffixKey;
  name: LocalizedText;
  /** Description with `{v}` standing for the rolled value in percent. */
  text: LocalizedText;
  /** Value in percent at tier D before the tier multiplier and the roll. */
  base: number;
  /** Relative chance of rolling this bonus on a module from each workshop; every entry is above zero. */
  weights: Record<Manufacturer, number>;
}

function affix(key: AffixKey, name: [string, string], text: [string, string], base: number, weights: Record<Manufacturer, number>): AffixDef {
  return { key, name: { de: name[0], en: name[1] }, text: { de: text[0], en: text[1] }, base, weights };
}

export const AFFIXES: readonly AffixDef[] = [
  affix("limit_charge", ["Überladung", "Overcharge"],
    ["Die Limit-Leiste füllt sich um {v}% schneller (Taktikfeld: kürzere Abklingzeit der Rollen-Aktion).", "The limit bar fills {v}% faster (tactical field: shorter role-action cooldown)."],
    12, { KRG: 5, VLK: 4, OMC: 2, HEX: 1, RDL: 1 }),
  affix("limit_power", ["Limitverstärker", "Limit Amplifier"],
    ["Der Limitbruch schlägt um {v}% härter zu (Taktikfeld: stärkere Rollen-Aktion).", "The limit break hits {v}% harder (tactical field: stronger role action)."],
    15, { KRG: 4, VLK: 5, OMC: 1, HEX: 2, RDL: 1 }),
  affix("opening_burst", ["Eröffnungssalve", "Opening Salvo"],
    ["{v}% mehr Schaden in den ersten zwanzig Sekunden (Taktikfeld: in den ersten zwei Runden).", "{v}% more damage in the first twenty seconds (tactical field: the first two rounds)."],
    18, { RDL: 5, HEX: 3, KRG: 2, VLK: 1, OMC: 1 }),
  affix("last_stand", ["Letztes Gefecht", "Last Stand"],
    ["Unter einem Drittel Leben {v}% mehr Angriff.", "{v}% more attack below a third of health."],
    20, { OMC: 4, HEX: 4, RDL: 3, KRG: 1, VLK: 1 }),
  affix("executioner", ["Vollstrecker", "Executioner"],
    ["{v}% mehr Schaden gegen verwundete Ziele.", "{v}% more damage against wounded targets."],
    16, { HEX: 5, VLK: 3, RDL: 3, KRG: 2, OMC: 1 }),
  affix("lifesteal", ["Nanotransfusion", "Nano Transfusion"],
    ["Heilt {v}% des angerichteten Schadens.", "Heals {v}% of the damage dealt."],
    6, { OMC: 5, KRG: 3, VLK: 2, HEX: 1, RDL: 1 }),
  affix("thorns", ["Reaktivpanzer", "Reactive Plating"],
    ["Gibt {v}% des erlittenen Schadens zurück.", "Returns {v}% of the damage taken."],
    10, { RDL: 5, HEX: 4, OMC: 2, KRG: 1, VLK: 1 }),
  affix("shieldbreaker", ["Schildbrecher", "Shieldbreaker"],
    ["{v}% mehr Schaden gegen abgeschirmte Ziele.", "{v}% more damage against shielded targets."],
    22, { HEX: 4, RDL: 4, VLK: 3, KRG: 2, OMC: 2 }),
];

const byKey = new Map(AFFIXES.map((a) => [a.key, a]));

export function affixByKey(key: AffixKey): AffixDef {
  const found = byKey.get(key);
  if (!found) throw new Error(`unknown affix ${key}`);
  return found;
}

/** How many special bonuses a module of each tier carries; legendary gear may carry three (D77). */
export const AFFIX_SLOTS: Record<Tier, { min: number; max: number }> = {
  D: { min: 0, max: 0 },
  C: { min: 1, max: 1 },
  B: { min: 1, max: 1 },
  A: { min: 1, max: 2 },
  S: { min: 1, max: 3 },
};

/** Spread of a roll around the tier value: four fifths to six fifths, so two identical pieces rarely are. */
export const AFFIX_ROLL_SPREAD = 0.2;

/**
 * The value a bonus carries. `roll` runs 0..100 and is the randomness the player feels: the same legendary
 * blade is worth hunting twice because the second one may roll higher.
 */
export function affixValue(key: AffixKey, tier: Tier, roll: number): number {
  const def = affixByKey(key);
  const clamped = Math.max(0, Math.min(100, roll));
  const spread = 1 - AFFIX_ROLL_SPREAD + (clamped / 100) * (AFFIX_ROLL_SPREAD * 2);
  return Math.max(1, Math.round(def.base * TIER_STAT_MULT[tier] * spread));
}

/** Total weight of one workshop's bonus table, used by the roller. */
export function affixWeightTotal(mfr: Manufacturer): number {
  return AFFIXES.reduce((sum, a) => sum + a.weights[mfr], 0);
}

export const AFFIX_MANUFACTURERS = MANUFACTURER_IDS;
